import { type ReactNode, useState, useEffect } from 'react';

type AlertVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';

interface AlertProps {
    variant: AlertVariant;
    children: ReactNode;
    dismissible?: boolean;
    autoHide?: boolean;
    autoHideDelay?: number; // milliseconds
    onClose?: () => void;
    className?: string;
    icon?: ReactNode;
    heading?: string;
    isOpen?: boolean;
}

const Alert = ({
    variant,
    children,
    dismissible = false,
    autoHide = false,
    autoHideDelay = 5000,
    onClose,
    className = '',
    icon,
    heading,
    isOpen = true
}: AlertProps) => {
    const [visible, setVisible] = useState(isOpen);

    // Handle close button click
    const handleClose = () => {
        setVisible(false);

        if (onClose) {
            onClose();
        }
    };

    // Handle auto-hide functionality
    useEffect(() => {
        if (!visible || !autoHide) return;

        const timer = setTimeout(() => {
            handleClose();
        }, autoHideDelay);

        return () => clearTimeout(timer);
    }, [visible, autoHide, autoHideDelay]);

    // Update visibility if isOpen prop changes
    useEffect(() => {
        setVisible(isOpen);
    }, [isOpen]);

    if (!visible) {
        return null;
    }

    // Get the appropriate Tailwind classes based on variant
    const getVariantClasses = () => {
        switch (variant) {
            case 'primary':
                return 'bg-blue-600 text-white border-blue-700';
            case 'secondary':
                return 'bg-zinc-600 text-white border-zinc-700';
            case 'success':
                return 'bg-green-600 text-white border-green-700';
            case 'danger':
                return 'bg-red-600 text-white border-red-700';
            case 'warning':
                return 'bg-yellow-500 text-black border-yellow-600';
            case 'info':
                return 'bg-cyan-500 text-white border-cyan-600';
            case 'light':
                return 'bg-zinc-100 text-zinc-800 border-zinc-200';
            case 'dark':
                return 'bg-zinc-800 text-white border-zinc-900';
            default:
                return 'bg-blue-600 text-white border-blue-700';
        }
    };

    return (
        <div className={`px-4 py-3 mb-4 rounded relative border ${getVariantClasses()} ${className}`} role="alert">
            {icon && <div className="mr-2 inline-block">{icon}</div>}
            <div className="inline-block">
                {heading && <h5 className="text-lg font-semibold mb-1">{heading}</h5>}
                {children}
            </div>
            {dismissible && (
                <button
                    aria-label="Close"
                    className="absolute top-2 right-2 text-current hover:text-zinc-100 focus:outline-none"
                    type="button"
                    onClick={handleClose}
                >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                    </svg>
                </button>
            )}
        </div>
    );
};

// Create a component to globally manage alerts
export interface AlertOptions extends Omit<AlertProps, 'children' | 'isOpen'> {
    message: ReactNode;
    id?: string;
}

// Create a global alert event system
type AlertEvent = {
    type: 'add' | 'remove';
    alert?: AlertOptions;
    id?: string;
};

// Global event name
export const ALERT_EVENT = 'global-alert-event';

// Create an alert
export const showAlert = (options: AlertOptions) => {
    const id = options.id || `alert-${Date.now()}`;

    const event = new CustomEvent<AlertEvent>(ALERT_EVENT, {
        detail: {
            type: 'add',
            alert: {
                ...options,
                id,
                // Default alert to be dismissible and auto-hide
                dismissible: options.dismissible !== undefined ? options.dismissible : true,
                autoHide: options.autoHide !== undefined ? options.autoHide : true
            }
        }
    });

    document.dispatchEvent(event);
    return id;
};

// Hide an alert by ID
export const hideAlert = (id: string) => {
    const event = new CustomEvent<AlertEvent>(ALERT_EVENT, {
        detail: { type: 'remove', id }
    });

    document.dispatchEvent(event);
};

export default Alert;
